import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { IViewPortController } from '@/engine/app/interfaces';
import { SyntheticEvent } from './SyntheticEvent';
import { PointerSyntheticEvent } from './PointerSyntheticEvent';
import { Vector2 } from 'three';
import * as THREE from 'three';
import type { ContextEvents } from '@/engine/app/modules/images/interfaces.ts';

export interface IPointerHandler {
  onDblClick?: (event: PointerSyntheticEvent) => void;
  setParent: (ctrl: IPointerController) => void;
  onPointerDown?: (event: PointerSyntheticEvent) => void;
  onPointerUp?: (event: PointerSyntheticEvent) => void;
  onPointerMove?: (event: PointerSyntheticEvent) => void;
  onPointerCancel?: (event: PointerSyntheticEvent) => void;
  onPointerEnter?: (event: PointerSyntheticEvent) => void;
  onPointerLeave?: (event: PointerSyntheticEvent) => void;
  onContextCommand?: (event: PointerSyntheticEvent) => void;
  onWheel?: (event: SyntheticEvent<WheelEvent>) => void;
  onFixedClick?: (event: PointerSyntheticEvent) => void;
  onLongPress?: (event: PointerSyntheticEvent) => void;
}

export interface IPointerController {
  pushPointerHandler: (handler: IPointerHandler, priority?: number) => IHandlerDescriptor;
  setCursor: (cursor: string) => void;
  setContextState: (state: ContextEvents) => void;
  setPointerCapture: (pointerId: number) => void;
  releasePointerCapture: (pointerId: number) => void;
  isPointerDown: () => boolean;
}

export interface IHandlerDescriptor {
  remove: () => void;
}

type _EventHandlerProxy<TEvent extends Event> = (handler: IPointerHandler, event: SyntheticEvent<TEvent>) => void;

class _LongPress {
  private _isPressed = false;

  private _executed = false;

  private static _pressTimeMs = 400;

  public runDelayed<T>(run: () => T): Promise<T> {
    this._isPressed = true;

    return new Promise((resolve) => {
      setTimeout(() => {
        if (this._isPressed) {
          this._executed = true;
          resolve(run());
        }
      }, _LongPress._pressTimeMs);
    });
  }

  public release(): void {
    this._isPressed = false;
    this._executed = false;
  }

  public get executed(): boolean {
    return this._executed;
  }
}

export class PointerController implements IPointerController {
  private _viewPortController: IViewPortController;

  private _dependencies: DisposablesBasket = new DisposablesBasket();

  private _handlersStack: Array<{ value: IPointerHandler; priority: number }> = [];

  private _defaultHandler: IPointerHandler;

  private _pointerDownCoords: THREE.Vector2 | undefined;

  private _viewPortDescriptor: string;

  private readonly _fixedClickDistance = 4;

  private _isPointerDown = false;

  private _longPress = new _LongPress();

  public constructor(
    viewPortController: IViewPortController,
    defaultHandler: IPointerHandler,
    viewPortDescriptor: string
  ) {
    this._viewPortController = viewPortController;
    this._defaultHandler = defaultHandler;
    this._viewPortDescriptor = viewPortDescriptor;
    this._defaultHandler.setParent(this);

    this._dependencies.add(
      this._viewPortController.addEventListener('pointerdown', (event: Event) => {
        this._pointerDownCoords = new THREE.Vector2((event as PointerEvent).clientX, (event as PointerEvent).clientY);
        this._isPointerDown = true;
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerDown?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        ); // To prevent fixedClick handling after pointerDown was stopped propagating

        if ((event as PointerEvent).pointerType === 'touch') {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
          this._longPress.runDelayed(() => {
            return this._handleEvent(
              (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
                handler.onLongPress?.(syntheticEvent as PointerSyntheticEvent);
              },
              this._createPointerSyntheticEvent(event as PointerEvent)
            );
          });
        }
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointerup', (event: Event) => {
        this._isPointerDown = false;

        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerUp?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
        const distance = this._pointerDownCoords?.distanceTo(
          new THREE.Vector2((event as PointerEvent).clientX, (event as PointerEvent).clientY)
        );
        if (distance !== undefined && distance < this._fixedClickDistance && !this._longPress.executed) {
          this._handleEvent(
            (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
              handler.onFixedClick?.(syntheticEvent as PointerSyntheticEvent);
            },
            this._createPointerSyntheticEvent(event as PointerEvent)
          );
        }
        if ((event as PointerEvent).pointerType === 'touch') {
          this._longPress.release();
        }
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointermove', (event: Event) => {
        if ((event as PointerEvent).pointerType === 'touch') {
          this._longPress.release();
        }
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerMove?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointerleave', (event: Event) => {
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerLeave?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointerenter', (event: Event) => {
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerEnter?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointercancel', (event: Event) => {
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerCancel?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('dblclick', (event: Event) => {
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onDblClick?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('wheel', (event: Event) => {
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<WheelEvent>): void => {
            handler.onWheel?.(syntheticEvent);
          },
          new SyntheticEvent(event as WheelEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('contextmenu', (event: Event) => {
        event.preventDefault();
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('contextCommand', (event: Event) => {
        this._handleEvent(
          (handler: IPointerHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onContextCommand?.(syntheticEvent as PointerSyntheticEvent);
          },
          this._createPointerSyntheticEvent(event as PointerEvent)
        );
      })
    );
  }

  public isPointerDown(): boolean {
    return this._isPointerDown;
  }

  public pushPointerHandler(handler: IPointerHandler, priority?: number): IHandlerDescriptor {
    handler.setParent(this);
    // use wrapper also to support several entries of the same handler stored in stack
    const wrapper = { value: handler, priority: priority ?? 0 };
    // find the index to insert new element at,
    // so that stack is being kept sorted in respect
    // to priorities and order of pushes
    const index = this._handlersStack.findIndex((value) => value.priority <= wrapper.priority);
    // append to the bottom of the stack if not found (index=-1)
    this._handlersStack.splice(index >= 0 ? index : this._handlersStack.length, 0, wrapper);
    return {
      remove: (): void => {
        this._handlersStack = this._handlersStack.filter((h) => h !== wrapper);
      }
    };
  }

  public setCursor(cursor: string): void {
    this._viewPortController.setCursor(cursor);
  }

  public setContextState(state: ContextEvents): void {
    this._viewPortController.setContextMenuState(state);
  }

  public setPointerCapture(pointerId: number): void {
    this._viewPortController.setPointerCapture(pointerId);
  }

  public releasePointerCapture(pointerId: number): void {
    this._viewPortController.releasePointerCapture(pointerId);
  }

  public dispose(): void {
    this._dependencies.dispose();
  }

  private _handleEvent<TEvent extends MouseEvent>(
    handlerProxy: _EventHandlerProxy<TEvent>,
    eventInstance: SyntheticEvent<TEvent>
  ): void {
    for (const handler of [...this._handlersStack]) {
      // use cloning here to support self handlers stack pushes
      handlerProxy(handler.value, eventInstance);
      if (eventInstance.isPropagationStopped) {
        break;
      }
    }

    if (!eventInstance.isDefaultPrevented) {
      handlerProxy(this._defaultHandler, eventInstance);
    }
  }

  private _createPointerSyntheticEvent(event: PointerEvent): PointerSyntheticEvent {
    const { offsetX, offsetY } = event;
    const ray: THREE.Ray = this._viewPortController.getCameraController().getViewRay(new Vector2(offsetX, offsetY));
    return new PointerSyntheticEvent(event, ray, this._viewPortDescriptor);
  }
}
